import { position, autoUpdate } from '../floating.js';
import { filterOptions, ActiveOption, onOutsidePointer } from '../listbox.js';

/**
 * <mellow-autocomplete>: a text input with a list of suggestions (WAI-ARIA combobox with
 * list autocomplete). The value is the input's text, so wire:model binds to the input
 * natively. With [data-no-filter] the list is not filtered client-side (e.g. when
 * Livewire returns matching items).
 */
export class MellowAutocomplete extends HTMLElement {
    connectedCallback() {
        if (this._ready) return this.sync();
        this._ready = true;

        this.active = new ActiveOption(this, () => this.input);

        this.input?.addEventListener('input', () => {
            if (this._choosing) return;
            this.refresh();
            if (this.active.options.length) this.open();
            else this.close();
        });
        this.input?.addEventListener('keydown', this.onKeydown);
        this.input?.addEventListener('click', () => this.open());

        this.listbox?.addEventListener('pointerdown', (event) => event.preventDefault());
        this.listbox?.addEventListener('click', (event) => this.choose(event.target.closest('[role="option"]')));
        this.listbox?.addEventListener('pointermove', (event) => {
            const option = event.target.closest('[role="option"]');
            if (option) this.active.set(option, false);
        });
    }

    get input() {
        return this.querySelector('input[role="combobox"]');
    }

    get panel() {
        return this.querySelector(':scope > [popover]');
    }

    get listbox() {
        return this.querySelector('[role="listbox"]');
    }

    get isOpen() {
        return this.panel?.matches(':popover-open') ?? false;
    }

    /** After a Livewire update (e.g. server-side filtering) keep the list consistent. */
    sync() {
        if (!this.active) return;
        this.refresh();
        this.active.refresh();
        if (document.activeElement === this.input && this.input.value && this.active.options.length) this.open();
        if (this.isOpen && !this.active.options.length) this.close();
    }

    refresh() {
        if (!this.hasAttribute('data-no-filter')) filterOptions(this.listbox, this.input.value);
        this.active.refresh();
    }

    open() {
        if (this.isOpen || !this.active.options.length) return;

        this.panel.showPopover();
        this.input.setAttribute('aria-expanded', 'true');
        const anchor = this.input.closest('.mellow-input') ?? this.input;
        this.panel.style.minWidth = `${anchor.offsetWidth}px`;
        this._cleanup = autoUpdate(anchor, this.panel, () => position(anchor, this.panel, { placement: 'bottom', align: 'start', offset: 4 }));
        this._outside = onOutsidePointer(this, () => this.close());
    }

    close() {
        if (!this.isOpen) return;
        this.panel.hidePopover();
        this.input.setAttribute('aria-expanded', 'false');
        this.active.set(null);
        this._cleanup?.();
        this._outside?.();
    }

    choose(option) {
        if (!option || option.getAttribute('aria-disabled') === 'true') return;

        // Setting the value fires `input` for wire:model without re-opening the list.
        this._choosing = true;
        this.input.value = option.dataset.value;
        this.input.dispatchEvent(new Event('input', { bubbles: true }));
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
        this._choosing = false;

        this.close();
        this.input.focus();
        this.dispatchEvent(new CustomEvent('mellow:select', { bubbles: true, detail: option.dataset.value }));
    }

    onKeydown = (event) => {
        switch (event.key) {
            case 'ArrowDown':
            case 'ArrowUp':
                event.preventDefault();
                if (!this.isOpen) {
                    this.refresh();
                    this.open();
                }
                if (event.altKey) return;
                return this.active.move(event.key === 'ArrowDown' ? 1 : -1);
            case 'Enter':
                if (this.isOpen && this.active.current) {
                    event.preventDefault();
                    this.choose(this.active.current);
                }
                return;
            case 'Escape':
                // First Escape closes the list, a second one clears the input (per ARIA).
                if (this.isOpen) {
                    event.preventDefault();
                    this.close();
                } else if (this.input.value) {
                    event.preventDefault();
                    this.input.value = '';
                    this.input.dispatchEvent(new Event('input', { bubbles: true }));
                }
                return;
            case 'Tab':
                return this.close();
        }
    };
}

/**
 * <mellow-command>: an always visible, filterable list of actions. Enter runs the
 * highlighted item (click); items inside a modal close it unless [data-keep-open].
 */
export class MellowCommand extends HTMLElement {
    connectedCallback() {
        if (this._ready) return;
        this._ready = true;

        this.active = new ActiveOption(this, () => this.input);

        this.addEventListener('input', (event) => {
            if (event.target !== this.input) return;
            const visible = filterOptions(this, this.input.value);
            const empty = this.querySelector('.mellow-listbox-empty');
            if (empty) empty.hidden = visible > 0;
            this.active.first();
        });

        this.addEventListener('keydown', (event) => {
            if (event.target !== this.input) return;

            switch (event.key) {
                case 'ArrowDown':
                    event.preventDefault();
                    return this.active.move(1);
                case 'ArrowUp':
                    event.preventDefault();
                    return this.active.move(-1);
                case 'Enter':
                    event.preventDefault();
                    return this.active.current?.click();
            }
        });

        this.addEventListener('pointermove', (event) => {
            const option = event.target.closest('[role="option"]');
            if (option) this.active.set(option, false);
        });

        this.addEventListener('click', (event) => {
            const option = event.target.closest('[role="option"]');
            if (!option || option.getAttribute('aria-disabled') === 'true') return;
            if (!option.hasAttribute('data-keep-open')) option.closest('mellow-modal')?.close();
        });

        // Reset search when the surrounding modal opens again.
        this.closest('mellow-modal')?.addEventListener('mellow:open', () => this.reset(true));

        this.reset(false);
    }

    get input() {
        return this.querySelector('[data-mellow-command-input]');
    }

    reset(focus) {
        if (this.input) this.input.value = '';
        filterOptions(this, '');
        const empty = this.querySelector('.mellow-listbox-empty');
        if (empty) empty.hidden = true;
        this.active.first();
        if (focus) this.input?.focus({ preventScroll: true });
    }
}

/** Global keyboard shortcuts: [data-mellow-shortcut="mod+k"] clicks the element. */
export function registerShortcuts() {
    const mac = /Mac|iPhone|iPad/.test(navigator.platform);

    // "mod" is ⌘ on Apple platforms and Ctrl elsewhere; modifiers must match exactly.
    const matches = (shortcut, event) => {
        const parts = shortcut.toLowerCase().split('+');
        const key = parts.pop();
        const want = { ctrl: false, meta: false, alt: false, shift: false };
        parts.forEach((part) => (want[part === 'mod' ? (mac ? 'meta' : 'ctrl') : part] = true));

        return event.key.toLowerCase() === key
            && event.ctrlKey === want.ctrl
            && event.metaKey === want.meta
            && event.altKey === want.alt
            && event.shiftKey === want.shift;
    };

    document.addEventListener('keydown', (event) => {
        for (const el of document.querySelectorAll('[data-mellow-shortcut]')) {
            if (matches(el.dataset.mellowShortcut, event)) {
                event.preventDefault();
                el.click();
                return;
            }
        }
    });
}
